import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import { FaIcon } from './FaIcon';

/**
 * ErrorBoundary component that catches JavaScript errors anywhere in the component tree,
 * logs those errors, and displays a fallback UI instead of the component tree that crashed.
 *
 * El fallback usa el design system de Dashbo (paleta + Montserrat/Source Sans Pro +
 * botones .btn). El stack trace solo se muestra cuando `showDetails` es true
 * (en App.js: `process.env.NODE_ENV === 'development'`), así un usuario en producción
 * nunca ve detalles técnicos.
 */
class ErrorBoundaryComponent extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null, errorInfo: null };
    }

    static getDerivedStateFromError(error) {
        // Update state so the next render will show the fallback UI
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        // Log the error to the console or an error reporting service
        console.error('ErrorBoundary caught an error:', error, errorInfo);

        this.setState({
            error: error,
            errorInfo: errorInfo
        });

        // You can also log the error to an error reporting service here
        Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } });
    }

    handleReload = () => {
        window.location.reload();
    }

    render() {
        const { t } = this.props;

        if (!this.state.hasError) {
            return this.props.children;
        }

        const showDetails = this.props.showDetails && this.state.error;

        return (
            <div className="error-boundary-screen">
                <div className="error-boundary-card" role="alert" aria-live="assertive">
                    <div className="error-boundary-card__icon">
                        <FaIcon iconName="triangle-exclamation" />
                    </div>

                    <h1 className="error-boundary-card__title">
                        {t('error_boundary.something_went_wrong')}
                    </h1>

                    <p className="error-boundary-card__message">
                        {t('error_boundary.unexpected_error_message')}
                    </p>

                    <div className="error-boundary-card__actions">
                        <button type="button" className="btn" onClick={this.handleReload}>
                            {t('error_boundary.reload_page')}
                        </button>
                    </div>

                    {showDetails && (
                        <details className="error-boundary-card__details">
                            <summary>
                                <span className="error-boundary-card__dev-badge">
                                    {t('error_boundary.dev_only_badge')}
                                </span>
                                {t('error_boundary.error_details')}
                                <FaIcon iconName="chevron-right" className="error-boundary-card__caret" />
                            </summary>
                            <pre className="error-boundary-card__trace">
                                {this.state.error.toString()}
                                {this.state.errorInfo && this.state.errorInfo.componentStack}
                            </pre>
                        </details>
                    )}
                </div>
            </div>
        );
    }
}

export const ErrorBoundary = withTranslation()(ErrorBoundaryComponent);
